/**
 * Quest metadata generator — mirrors `agent-metadata-generator.ts` but for
 * quests. Spawns a small internal agent that returns a short JSON-encoded
 * label for the quest based on the user prompt, then applies it via
 * `QuestService.setName()`.
 *
 * Auto-naming is skipped if the user already supplied an explicit name (we
 * don't have a UI for that yet, but the parameter is wired up for symmetry
 * with the agent metadata generator).
 */

import { z } from "zod";
import type { Logger } from "pino";

import type { QuestService } from "./service.js";
import {
  DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
  StructuredAgentFallbackError,
  StructuredAgentResponseError,
  generateStructuredAgentResponseWithFallback,
} from "../agent/agent-response-loop.js";

export const MAX_AUTO_QUEST_NAME_CHARS = 40;

export type QuestMetadataGeneratorDeps = {
  generateStructuredAgentResponseWithFallback?: typeof generateStructuredAgentResponseWithFallback;
};

export type QuestMetadataGenerationOptions = {
  questService: QuestService;
  questId: string;
  cwd: string;
  prompt: string;
  explicitName?: string | null;
  logger: Logger;
  deps?: QuestMetadataGeneratorDeps;
  /** auth-server user-id of the caller — stamped on the internal agent. */
  ownerUserId: string;
};

function hasExplicitName(name?: string | null): boolean {
  return Boolean(name && name.trim().length > 0);
}

function normalizeAutoName(name: string): string | null {
  const normalized = name.trim();
  if (!normalized) {
    return null;
  }
  return normalized.slice(0, MAX_AUTO_QUEST_NAME_CHARS).trim() || null;
}

function buildPrompt(prompt: string): string {
  return [
    "Generate a short label for a multi-agent quest based on the user prompt.",
    `Name: short descriptive label (<= ${MAX_AUTO_QUEST_NAME_CHARS} chars).`,
    "Return JSON only with a single field 'name'.",
    "",
    "User prompt:",
    prompt,
  ].join("\n");
}

export async function generateAndApplyQuestMetadata(
  options: QuestMetadataGenerationOptions,
): Promise<void> {
  const prompt = options.prompt.trim();
  if (!prompt) {
    return;
  }
  if (hasExplicitName(options.explicitName)) {
    return;
  }

  const schema = z.object({
    name: z.string().min(1).max(MAX_AUTO_QUEST_NAME_CHARS),
  });

  const generator =
    options.deps?.generateStructuredAgentResponseWithFallback ??
    generateStructuredAgentResponseWithFallback;

  let result: { name?: string };
  try {
    result = await generator({
      manager: options.questService.agentManager,
      cwd: options.cwd,
      prompt: buildPrompt(prompt),
      schema,
      schemaName: "QuestMetadata",
      maxRetries: 2,
      providers: DEFAULT_STRUCTURED_GENERATION_PROVIDERS,
      ownerUserId: options.ownerUserId,
      agentConfigOverrides: {
        title: "Quest metadata generator",
        internal: true,
      },
    });
  } catch (error) {
    if (
      error instanceof StructuredAgentResponseError ||
      error instanceof StructuredAgentFallbackError
    ) {
      options.logger.warn(
        { err: error, questId: options.questId },
        "Structured quest metadata generation failed",
      );
      return;
    }
    options.logger.error(
      { err: error, questId: options.questId },
      "Quest metadata generation failed",
    );
    return;
  }

  if (typeof result.name !== "string") {
    return;
  }
  const normalized = normalizeAutoName(result.name);
  if (!normalized) {
    return;
  }

  try {
    await options.questService.setName(options.questId, normalized);
  } catch (error) {
    options.logger.warn(
      { err: error, questId: options.questId },
      "Failed to apply generated quest name",
    );
  }
}

export function scheduleQuestMetadataGeneration(options: QuestMetadataGenerationOptions): void {
  queueMicrotask(() => {
    void generateAndApplyQuestMetadata(options).catch((error) => {
      options.logger.error(
        { err: error, questId: options.questId },
        "Quest metadata generation crashed",
      );
    });
  });
}
